import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiClient } from '@api';
import { useTrans } from '@libs/i18n';
import {
  ApplyClassificationCandidateParam,
  AssessmentDomainDocument,
  ConfirmUploadBody,
  ConfirmUploadParam,
  CreateUploadUrlBody,
  CreateUploadUrlParam,
  CreateUploadUrlResponse,
  DeleteDocumentParam,
  GetAllAssessmentDomainDocsParam,
  GetAllAssessmentDomainDocsResponse,
  GetDocumentDownloadUrlParam,
  MoveDocumentToSuggestedDomainParam,
} from '@libs/types';

import { getLocalizedErrorMessage } from '../utils/error-utils';

// ============================================================================
// SERVICE FUNCTIONS - Document Operations
// ============================================================================

/**
 * Get all documents for an assessment domain
 * Input: assessmentId (string), assessmentDomainId (string)
 * Output: List of documents with metadata
 */
async function getAllAssessmentDomainDocuments(
  params: GetAllAssessmentDomainDocsParam,
  signal?: AbortSignal,
): Promise<GetAllAssessmentDomainDocsResponse> {
  const response = await apiClient.get<GetAllAssessmentDomainDocsResponse>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/documents`,
    {
      signal,
    },
  );

  return response.data;
}

/**
 * Create upload URL for document
 * Input: assessmentId (string), assessmentDomainId (string), fileName (string), contentType (string)
 * Output: Presigned S3 upload URL, s3Key, and documentId
 */
async function createUploadUrl(
  params: CreateUploadUrlParam,
  data: CreateUploadUrlBody,
): Promise<CreateUploadUrlResponse> {
  const response = await apiClient.post<CreateUploadUrlResponse>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/documents/create-upload-url`,
    data,
  );

  return response.data;
}

/**
 * Confirm document upload after S3 upload completes
 * Input: assessmentId (string), assessmentDomainId (string), documentId (string), fileName (string), fileSize (number), contentType (string), s3Key (string)
 * Output: Confirmation with documentId
 */
async function confirmUpload(
  params: ConfirmUploadParam,
  data: ConfirmUploadBody,
): Promise<AssessmentDomainDocument> {
  const response = await apiClient.post<AssessmentDomainDocument>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/documents/${params.documentId}/confirm-upload`,
    data,
  );

  return response.data;
}

/**
 * Delete a document from an assessment domain
 * Input: assessmentId (string), assessmentDomainId (string), documentId (string)
 * Output: Confirmation message
 */
async function deleteDocument(params: DeleteDocumentParam): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/documents/${params.documentId}`,
  );

  return response.data;
}

/**
 * Move a document to its AI-suggested domain (when the AI flagged it as
 * uploaded to the wrong domain). Avoids delete + re-upload.
 * Input: assessmentId, assessmentDomainId (current/source), documentId
 * Output: the moved document
 */
async function moveDocumentToSuggestedDomain(
  params: MoveDocumentToSuggestedDomainParam,
): Promise<AssessmentDomainDocument> {
  const response = await apiClient.patch<AssessmentDomainDocument>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/documents/${params.documentId}/move-to-suggested`,
  );

  return response.data;
}

/**
 * Match a document to one of the AI's ranked candidate standard documents,
 * inside the domain it already lives in. Used when a client file blends two or
 * three expected documents and the user picks which one it really is.
 * Input: assessmentId, assessmentDomainId, documentId, standardDocumentKey
 * Output: the updated document
 */
async function applyClassificationCandidate(
  params: ApplyClassificationCandidateParam,
): Promise<AssessmentDomainDocument> {
  const response = await apiClient.patch<AssessmentDomainDocument>(
    `api/assessments/${params.assessmentId}/domains/${params.assessmentDomainId}/documents/${params.documentId}/classification-candidate`,
    {
      standardDocumentKey: params.standardDocumentKey,
      // Omitted rather than sent empty when the candidate is in the current
      // domain: the API reads any value here as "this is a move", and an empty
      // string is not the same statement as no statement.
      ...(params.targetDomainKey ? { targetDomainKey: params.targetDomainKey } : {}),
    },
  );

  return response.data;
}

/**
 * Get presigned download URL for a document
 * Input: documentId (string)
 * Output: Presigned S3 download URL
 */
async function getDocumentDownloadUrl(
  params: GetDocumentDownloadUrlParam,
  signal?: AbortSignal,
): Promise<{ s3DownloadUrl: string }> {
  const response = await apiClient.get<{ s3DownloadUrl: string }>(
    `api/assessments/documents/${params.documentId}/download-url`,
    {
      signal,
    },
  );

  return response.data;
}

// ============================================================================
// REACT QUERY HOOKS - Document Operations
// ============================================================================

export const useGetAllAssessmentDomainDocuments = (params: GetAllAssessmentDomainDocsParam) => {
  return useQuery<GetAllAssessmentDomainDocsResponse>({
    queryKey: ['assessments', params.assessmentId, 'domains', params.assessmentDomainId, 'documents', params],
    queryFn: ({ signal }) => getAllAssessmentDomainDocuments(params, signal),
  });
};

export const useCreateUploadUrl = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    CreateUploadUrlResponse,
    Error,
    { params: CreateUploadUrlParam; data: CreateUploadUrlBody }
  >({
    mutationFn: ({ params, data }) => createUploadUrl(params, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to create upload URL');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useConfirmUpload = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    AssessmentDomainDocument,
    Error,
    { params: ConfirmUploadParam; data: ConfirmUploadBody }
  >({
    mutationFn: ({ params, data }) => confirmUpload(params, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to confirm upload');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<{ message: string }, Error, DeleteDocumentParam>({
    mutationFn: deleteDocument,
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: ['assessments', params.assessmentId, 'domains', params.assessmentDomainId, 'documents'],
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to delete document');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useMoveDocumentToSuggestedDomain = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AssessmentDomainDocument, Error, MoveDocumentToSuggestedDomainParam>({
    mutationFn: moveDocumentToSuggestedDomain,
    onSuccess: (_, params) => {
      // Invalidate the whole assessment so both the source and target domain
      // document lists refresh.
      queryClient.invalidateQueries({ queryKey: ['assessments', params.assessmentId] });
      enqueueSnackbar(t('app.assessment.classification.suggestion.moveSuccess'), {
        variant: 'success',
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(
        error,
        t,
        t('app.assessment.classification.suggestion.moveError'),
      );
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useApplyClassificationCandidate = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AssessmentDomainDocument, Error, ApplyClassificationCandidateParam>({
    mutationFn: applyClassificationCandidate,
    onSuccess: (_, params) => {
      // The matched key changes, which moves the domain's missing-documents list
      // and its duplicate-key checks — and when the chosen candidate lives in
      // another domain the document itself relocates, so a second domain's list
      // changes too. The whole assessment is refetched rather than just this row.
      queryClient.invalidateQueries({ queryKey: ['assessments', params.assessmentId] });
      enqueueSnackbar(t('app.assessment.classification.candidates.applySuccess'), {
        variant: 'success',
      });
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(
        error,
        t,
        t('app.assessment.classification.candidates.applyError'),
      );
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useGetDocumentDownloadUrl = (params: GetDocumentDownloadUrlParam) => {
  return useQuery<{ s3DownloadUrl: string }>({
    queryKey: ['assessments', 'documents', params.documentId, 'download-url', params],
    queryFn: ({ signal }) => getDocumentDownloadUrl(params, signal),
  });
};

export const useDownloadAllDocumentsZip = () => {
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, { assessmentId: string }>({
    mutationFn: async ({ assessmentId }) => {
      const response = await apiClient.get(
        `api/assessments/${assessmentId}/documents/download-zip`,
        { responseType: 'blob' },
      );

      const contentDisposition = response.headers['content-disposition'] as string | undefined;
      let filename = `documents_${assessmentId}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match?.[1]) filename = match[1];
      }

      const url = URL.createObjectURL(new Blob([response.data as BlobPart], { type: 'application/zip' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    },
    onError: (error) => {
      const errorMessage = getLocalizedErrorMessage(error, t, t('app.assessment.document.downloadAllFailed'));
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};
